import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatDateLabel,
  isQuestFailed,
  loadTodaysQuest,
  loadTodaysScheduleSummary,
  questResult,
  todayKey,
  type DailyQuest,
} from '../shared/daily';
import { DAILY_GAMES } from '../shared/games';
import { getResult, getStatuses, isFinished, pruneOlderThan } from '../shared/dailySession';
import HomeSeoContent from '../content/HomeSeoContent';
import Art from '../shared/ui/Art';
import { useMeta } from '../shared/useMeta';
import { SITE_BRAND } from '../shared/site';
import './hub.css';

const BASE = import.meta.env.BASE_URL;

function formatQuestTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Quest Gauntlet and Longshot, rendered as members of the game grid rather
 * than as banners above it. Same card, same states; the coloured kicker is
 * what says "not one of the fourteen".
 */
function FeatureTile({
  to,
  art,
  kicker,
  title,
  body,
  cta,
  variant,
  state,
}: {
  to?: string;
  art: string;
  kicker: string;
  title: string;
  body: string;
  cta: string;
  variant: 'quest' | 'longshot';
  state: 'open' | 'done' | 'failed';
}) {
  const className =
    `game-list-link hero-tile is-${variant}` +
    (state === 'done' ? ' is-locked' : state === 'failed' ? ' is-failed' : '');

  const inner = (
    <div className="game-list-item-paper">
      <div className="game-list-item">
        <div className="game-list-item-img">
          <Art name={art} />
        </div>
        <div className="game-list-item-info">
          <span className="hero-kicker">{kicker}</span>
          <h3 className="game-list-item-title">{title}</h3>
          <p className="game-list-item-desc">{body}</p>
          <div className="game-list-item-play">{cta}</div>
        </div>
      </div>
    </div>
  );

  // A failed quest is locked until tomorrow, so it is a tile, not a link.
  if (!to) return <div className={className}>{inner}</div>;
  return (
    <Link to={to} className={className}>
      {inner}
    </Link>
  );
}

export default function HubPage() {
  useMeta({
    title: `${SITE_BRAND}: Free Daily Word and Logic Games`,
    description:
      'A new set of free daily puzzles every day: cryptograms, word searches, grouping puzzles, number games and more. One attempt each, no sign-up.',
    path: '/',
    image: '/og-image.png',
  });

  const [schedule, setSchedule] = useState<{
    dateKey: string;
    isToday: boolean;
    gameIds: Set<string>;
  } | null>(null);
  const [quest, setQuest] = useState<DailyQuest | null>(null);
  const [, setNow] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadTodaysScheduleSummary().then((res) => {
      if (!cancelled && res) setSchedule(res);
    });
    loadTodaysQuest().then((q) => {
      if (!cancelled) setQuest(q);
    });
    pruneOlderThan(todayKey());
    const onFocus = () => setNow((n) => n + 1);
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const dateKey = schedule?.dateKey ?? todayKey();
  const isToday = schedule?.isToday ?? true;
  const statuses = getStatuses(DAILY_GAMES.map((g) => g.id), dateKey);

  const questDone = quest ? questResult(quest.dateKey) : null;
  const questFailed = quest ? isQuestFailed(quest.dateKey) : false;
  // A finished or failed quest stays locked until tomorrow's puzzle.
  const questLocked = Boolean(questDone) || questFailed;
  // Longshot keys on the local calendar day, not the schedule's fallback date:
  // its board comes from R2, independent of what Firestore has published.
  const longshotDay = todayKey();
  const longshotDone = isFinished('longshot', longshotDay);
  const longshotBody = longshotDone
    ? getResult('longshot', longshotDay)?.subtitle ?? 'Played today'
    : 'Five bullets. Five words. One ladder.';

  const questBody = questDone
    ? `Done in ${formatQuestTime(questDone.totalElapsed)}`
    : questFailed
    ? 'Quest failed — try again tomorrow'
    : `${quest?.steps.length ?? 4} puzzles. One shot. One timer.`;

  return (
    <div className="page hub">
      {/* Above everything, including the app icon: the two pages a visitor
          arriving from a search is usually after. */}
      <nav className="hub-nav" aria-label="Guides and answers">
        <Link to="/blog">Strategy guides</Link>
        <a href="/archive/cryptogram">Past answers</a>
      </nav>

      <div className="hub-banner" aria-hidden />

      <header className="header">
        <Art
          name="games/appIcon"
          className="header-app-icon"
          alt={SITE_BRAND}
          width={96}
          height={96}
          eager
        />
        <div className="header-logo-word">puzzo</div>
        <div className="header-logo-sub">everyday puzzles</div>
        <p className="header-tagline">
          Fourteen daily puzzles plus Longshot and the Quest Gauntlet — new every day.
        </p>
        <a
          className="header-appstore"
          href="https://apps.apple.com/us/app/everyday-puzzle-logic-test/id6760844657"
          target="_blank"
          rel="noreferrer"
          aria-label="Download on the App Store"
        >
          <img
            src={`${BASE}app-store-badge.svg`}
            alt="Download on the App Store"
            width={192}
            height={64}
          />
        </a>
        <div className="header-datechip">{formatDateLabel(dateKey, isToday)}</div>
      </header>

      <div className="game-list">
        <div className="game-list-content">
          {quest && (
            <FeatureTile
              to={questLocked ? undefined : '/gauntlet'}
              art="games/quest"
              kicker="Daily quest"
              title="Quest Gauntlet"
              body={questBody}
              cta={
                questLocked
                  ? questFailed
                    ? 'Locked until tomorrow'
                    : 'Completed ✓'
                  : 'Start the gauntlet ›'
              }
              variant="quest"
              state={questFailed ? 'failed' : questDone ? 'done' : 'open'}
            />
          )}

          <FeatureTile
            to="/play/longshot"
            art="games/longshot"
            kicker="Special event"
            title="Longshot"
            body={longshotBody}
            cta={longshotDone ? 'See your ticket ›' : 'Take your shot ›'}
            variant="longshot"
            state={longshotDone ? 'done' : 'open'}
          />

          {DAILY_GAMES.map((g) => {
            const published = schedule?.gameIds.has(g.id) ?? true;
            const outcome = statuses[g.id];
            const status = !published
              ? 'soon'
              : outcome === 'won'
              ? 'done'
              : outcome === 'lost'
              ? 'failed'
              : 'open';
            const statusLabel =
              status === 'done'
                ? 'solved today ✓'
                : status === 'failed'
                ? 'missed today ✕'
                : status === 'soon'
                ? 'not published yet'
                : 'play today ›';
            return (
              <Link
                key={g.id}
                to={`/play/${g.id}`}
                className={'game-list-link status-' + status}
              >
                <div className="game-list-item-paper">
                  <div className="game-list-item">
                    <div className="game-list-item-img">
                      <Art name={`games/${g.id}`} />
                    </div>
                    <div className="game-list-item-info">
                      <h3 className="game-list-item-title">{g.title}</h3>
                      <p className="game-list-item-desc">{g.tagline}</p>
                      <div className={'game-list-item-play status-' + status}>
                        {statusLabel}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}

          {/* Dark EP promo card */}
          <div className="game-list-link">
            <div className="game-list-item-paper ep-card">
              <Art name="games/appIcon" className="ep-card-icon" />
              <div className="ep-card-title">puzzo</div>
              <div className="ep-card-desc">Play every puzzle on your iPhone.</div>
              <div className="ep-card-cta">
                <a
                  href="https://apps.apple.com/us/app/everyday-puzzle-logic-test/id6760844657"
                  target="_blank"
                  rel="noreferrer"
                  className="ep-card-badge"
                >
                  Get on App Store
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crawlable description of the site and links to every game. The hub
          above is a grid of images, so without this the most important page on
          the site has no text on it. Prerendered from the same component. */}
      <HomeSeoContent />

      <footer className="footer">
        <nav className="footer-nav">
          <Link to="/privacy">Privacy</Link>
        </nav>
        © {new Date().getFullYear()} {SITE_BRAND}
      </footer>
    </div>
  );
}
